import {
  useGetSettingsQuery,
  usePatchSettingsMutation,
  useGetGridPresetsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

export default function GridSettings() {
  const s = useGetSettingsQuery();
  const presets = useGetGridPresetsQuery();
  const [patch, patchState] = usePatchSettingsMutation();
  const [local, setLocal] = useState<any>({});

  useEffect(() => {
    if (s.data) {
      const sd: any = s.data;
      const symsList: string[] = Array.isArray(sd.grid_trader_symbols) && sd.grid_trader_symbols.length
        ? sd.grid_trader_symbols
        : [sd.grid_trader_symbol || "BTC/USDT"];
      setLocal({
        grid_trader_enabled: !!sd.grid_trader_enabled,
        grid_trader_live_enabled: !!sd.grid_trader_live_enabled,
        grid_trader_symbols_text: symsList.join(", "),
        grid_trader_preset: sd.grid_trader_preset || "moderate",
        grid_trader_paper_equity_usdt: Number(sd.grid_trader_paper_equity_usdt ?? 1000),
        grid_trader_tick_interval_minutes: Number(sd.grid_trader_tick_interval_minutes ?? 15),
        grid_trader_gemini_consult: sd.grid_trader_gemini_consult !== false,
      });
    }
  }, [s.data]);

  async function save() {
    const { grid_trader_symbols_text, ...rest } = local;
    const symbols = String(grid_trader_symbols_text || "")
      .split(/[,\s]+/)
      .map((x: string) => x.trim().toUpperCase())
      .filter(Boolean)
      .map((x: string) => x.includes("/") ? x : `${x}/USDT`);
    await patch({ ...rest, grid_trader_symbols: symbols }).unwrap();
    s.refetch();
  }

  const presetDetail = presets.data?.presets?.[local.grid_trader_preset];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Grid Settings</h1>
        <p className="text-sm text-muted">
          Switch presets, change paper equity, gate live mode, control how often
          the engine ticks. Gemini consultation only fires on borderline regimes
          (cheap; ~once per few hours when used).
        </p>
      </header>

      <Card>
        <CardHeader><CardTitle className="text-base">Core</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Toggle label="Engine enabled (paper trades + tick scheduler)"
                  value={local.grid_trader_enabled}
                  onChange={(v) => setLocal({ ...local, grid_trader_enabled: v })} />
          <Toggle label="Live mode (real USDT/BTC orders)"
                  value={local.grid_trader_live_enabled}
                  onChange={(v) => setLocal({ ...local, grid_trader_live_enabled: v })}
                  warn />
          <Toggle label="Gemini consult on borderline regime"
                  value={local.grid_trader_gemini_consult}
                  onChange={(v) => setLocal({ ...local, grid_trader_gemini_consult: v })} />
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Symbols (comma-separated)">
              <input type="text" placeholder="BTC/USDT, BNB/USDT, SOL/USDT, PAXG/USDT"
                     className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5 font-mono"
                     value={local.grid_trader_symbols_text || ""}
                     onChange={(e) => setLocal({ ...local, grid_trader_symbols_text: e.target.value })} />
            </Field>
            <Field label="Preset">
              <select className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5"
                      value={local.grid_trader_preset}
                      onChange={(e) => setLocal({ ...local, grid_trader_preset: e.target.value })}>
                <option value="conservative">conservative (1H)</option>
                <option value="moderate">moderate (4H)</option>
                <option value="aggressive">aggressive (1D)</option>
              </select>
            </Field>
            <Field label="Paper equity (USDT)">
              <input type="number" min={100} step={100}
                     className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5"
                     value={local.grid_trader_paper_equity_usdt}
                     onChange={(e) => setLocal({ ...local, grid_trader_paper_equity_usdt: parseFloat(e.target.value) || 1000 })} />
            </Field>
            <Field label="Tick interval (min)">
              <input type="number" min={5} max={120}
                     className="w-full bg-bg text-fg border border-border rounded px-2 py-1.5"
                     value={local.grid_trader_tick_interval_minutes}
                     onChange={(e) => setLocal({ ...local, grid_trader_tick_interval_minutes: parseInt(e.target.value) || 15 })} />
            </Field>
          </div>
          <Button onClick={save} disabled={patchState.isLoading}>
            {patchState.isLoading ? "Saving…" : "Save"}
          </Button>
        </CardContent>
      </Card>

      {presetDetail && (
        <Card>
          <CardHeader><CardTitle className="text-base">Preset details · {local.grid_trader_preset}</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-xs overflow-x-auto bg-bg p-3 rounded">{JSON.stringify(presetDetail, null, 2)}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Toggle({ label, value, onChange, warn }: { label: string; value: boolean; onChange: (v: boolean) => void; warn?: boolean }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className={`text-sm ${warn ? "text-amber-300" : ""}`}>{label}</span>
    </label>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label className="text-sm block">
      <div className="text-muted text-xs mb-1">{label}</div>
      {children}
    </label>
  );
}
